import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Save, User, Mail, Phone, MapPin, Instagram, Facebook, Twitter, Stethoscope } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";

export default function ProfileEditor({ user, onSave, onClose }) {
  const [formData, setFormData] = useState({
    full_name: user?.full_name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    address: user?.address || '',
    city: user?.city || '',
    country: user?.country || '',
    instagram: user?.instagram || '',
    facebook: user?.facebook || '',
    twitter: user?.twitter || '',
    professional_type: user?.professional_type || '',
    professional_license: user?.professional_license || '',
    professional_specialty: user?.professional_specialty || '',
    professional_bio: user?.professional_bio || ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await onSave(formData);
      toast.success('Perfil actualizado');
      onClose();
    } catch (error) {
      toast.error('Error al actualizar perfil');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 100 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
      >
        <div className="flex items-center justify-between p-6 border-b sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2">
            <User className="w-5 h-5 text-emerald-600" />
            <h2 className="text-xl font-bold text-gray-900">Editar Perfil</h2>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto flex-1">
          <div className="space-y-6">
            {/* Datos Personales */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Datos Personales</h3>
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">
                    Nombre Completo
                  </label>
                  <Input
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    placeholder="Tu nombre completo"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">
                    Email
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="tu@email.com"
                      className="pl-10"
                      disabled
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">El email no se puede modificar</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">
                    Teléfono
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+54 11 1234-5678"
                      className="pl-10"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Dirección */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-emerald-600" />
                Dirección
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">
                    Dirección
                  </label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="Calle, número, piso, depto"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-2">
                      Ciudad
                    </label>
                    <Input
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      placeholder="Buenos Aires"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-2">
                      País
                    </label>
                    <Input
                      value={formData.country}
                      onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                      placeholder="Argentina"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Redes Sociales */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Redes Sociales</h3>
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">
                    Instagram
                  </label>
                  <div className="relative">
                    <Instagram className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      value={formData.instagram}
                      onChange={(e) => setFormData({ ...formData, instagram: e.target.value })}
                      placeholder="@usuario"
                      className="pl-10"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">
                    Facebook
                  </label>
                  <div className="relative">
                    <Facebook className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      value={formData.facebook}
                      onChange={(e) => setFormData({ ...formData, facebook: e.target.value })}
                      placeholder="facebook.com/usuario"
                      className="pl-10"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">
                    Twitter / X
                  </label>
                  <div className="relative">
                    <Twitter className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      value={formData.twitter}
                      onChange={(e) => setFormData({ ...formData, twitter: e.target.value })}
                      placeholder="@usuario"
                      className="pl-10"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Información Profesional */}
            {user?.is_professional && (
              <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Stethoscope className="w-5 h-5 text-blue-600" />
                  Información Profesional
                </h3>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-2">
                      Tipo de profesional *
                    </label>
                    <Select 
                      value={formData.professional_type}
                      onValueChange={(value) => setFormData({...formData, professional_type: value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecciona tu profesión" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="nutricionista">Nutricionista</SelectItem>
                        <SelectItem value="medico">Médico</SelectItem>
                        <SelectItem value="enfermero">Enfermero</SelectItem>
                        <SelectItem value="farmaceutico">Farmacéutico</SelectItem>
                        <SelectItem value="otro">Otro profesional de salud</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-2">
                      Número de matrícula/licencia *
                    </label>
                    <Input
                      value={formData.professional_license}
                      onChange={(e) => setFormData({...formData, professional_license: e.target.value})}
                      placeholder="MP 12345"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-2">
                      Especialidad
                    </label>
                    <Input
                      value={formData.professional_specialty}
                      onChange={(e) => setFormData({...formData, professional_specialty: e.target.value})}
                      placeholder="Ej: Nutrición deportiva"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-2">
                      Biografía profesional
                    </label>
                    <Textarea
                      value={formData.professional_bio}
                      onChange={(e) => setFormData({...formData, professional_bio: e.target.value})}
                      placeholder="Breve descripción de tu experiencia y enfoque profesional"
                      rows={3}
                    />
                  </div>

                  {!user?.professional_verified && (
                    <p className="text-xs text-amber-600 flex items-center gap-1">
                      <span>⏳</span>
                      Tu perfil está pendiente de verificación administrativa
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-3 mt-6 pt-6 border-t">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancelar
            </Button>
            <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-700">
              <Save className="w-4 h-4 mr-2" />
              Guardar Cambios
            </Button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}